import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import PropTypes from 'prop-types';
import calculateTotalDistance from '../../../shared/utils/calculateTotalDistance';
import RoundedNumberField from '../../../shared/ui/RoundedNumberField/RoundedNumberField.tsx';
import { defaultDataMileage, defaultMonthMileage, months, years } from './constants/dates';
import { addLeadingZero, createData, hasError, hasErrorIcon, validateFuelUsage } from './utils';

import CellInput from './components/CellInput/CellInput.tsx';
import { getMonthWeeks, splitArrayByWeeks } from './utils/date';

// Отображает Данные ТС по пробегу
function ReportingPanel({
  postVehicleWeekMileagesResourceParams,
  getVehicleWeekMileagesParams,
  patchVehicleWeekMileagesResourceParams,
  postVehicleMonthMileagesResourceParams,
  getVehicleMonthMileagesParams,
  patchVehicleMonthMileagesResourceParams,
  getTrackPointsResourceParams,
  vehicle,
  fuelNorm,
}) {
  const nowDate = new Date();

  const [month, setMonth] = useState(nowDate.getMonth() + 1);
  const [year, setYear] = useState(nowDate.getFullYear());
  const [isCreateNewMonthMileage, setIsCreateNewMonthMileage] = useState(false);
  const isTableEnabled = useMemo(() => !!(month && year && vehicle), [month, year, vehicle]);
  const [dataMileage, setDataMileage] = useState(defaultDataMileage);
  const [monthMileage, setMonthMileage] = useState(defaultMonthMileage);
  const [isCorrect, setIsCorrect] = useState(false);
  const [isLoadingGps, setIsLoadingGps] = useState(false);
  const [weekNumbers, setWeekNumbers] = useState([]);

  const [errorWeekNum, setErrorWeekNum] = useState(0);
  const [fieldFuelAvailable, setFieldFuelAvailable] = useState(0);

  const validateForm = (leftFuel) => {
    const numLeftFuel = Number(leftFuel);
    const currentLeftFuel = Number.isNaN(numLeftFuel) ? monthMileage.fuel : numLeftFuel;
    const { errorWeek, fuelAvailable } = validateFuelUsage(dataMileage, currentLeftFuel);
    setFieldFuelAvailable(fuelAvailable);
    setErrorWeekNum(errorWeek);
  };

  const rows = useMemo(() => {
    const normCharge = dataMileage.map((m) => Math.round(fuelNorm * (+m.workDist + +m.personalDist)) / 100);
    return [
      createData(
        'refuel',
        'Заправлено, л',
        dataMileage[0].refuel,
        dataMileage[1].refuel,
        dataMileage[2].refuel,
        dataMileage[3].refuel,
        dataMileage[4].refuel,
        dataMileage[5].refuel,
      ),
      createData(
        'fuelCharge',
        'Фактический расход, л',
        dataMileage[0].fuelCharge,
        dataMileage[1].fuelCharge,
        dataMileage[2].fuelCharge,
        dataMileage[3].fuelCharge,
        dataMileage[4].fuelCharge,
        dataMileage[5].fuelCharge,
      ),
      createData('oilNormal', 'Расчет бензина по норме, л', ...normCharge),
      createData(
        'personalDist',
        'Личный пробег, км',
        dataMileage[0].personalDist,
        dataMileage[1].personalDist,
        dataMileage[2].personalDist,
        dataMileage[3].personalDist,
        dataMileage[4].personalDist,
        dataMileage[5].personalDist,
      ),
      createData(
        'workDist',
        'Рабочий пробег, км',
        dataMileage[0].workDist,
        dataMileage[1].workDist,
        dataMileage[2].workDist,
        dataMileage[3].workDist,
        dataMileage[4].workDist,
        dataMileage[5].workDist,
      ),
      createData(
        'gpsDist',
        'Проверочные показания по пробегу с GPS, км',
        dataMileage[0].gpsDist,
        dataMileage[1].gpsDist,
        dataMileage[2].gpsDist,
        dataMileage[3].gpsDist,
        dataMileage[4].gpsDist,
        dataMileage[5].gpsDist,
      ),
      createData(
        'odometerDist',
        'Счетчик пробега, км',
        dataMileage[0].odometerDist,
        dataMileage[1].odometerDist,
        dataMileage[2].odometerDist,
        dataMileage[3].odometerDist,
        dataMileage[4].odometerDist,
        dataMileage[5].odometerDist,
      ),
    ];
  }, [dataMileage, fuelNorm]);

  const setMileageOfNewMonth = (value) => {
    setMonthMileage((oldData) => ({
      ...oldData,
      odometerDist: +value,
    }));
  };

  const setOilOfNewMonth = (value) => {
    validateForm(value);
    setMonthMileage((oldData) => ({
      ...oldData,
      fuel: +value,
    }));
  };

  const onChangeHandlerInputs2 = (e, row, number) => {
    if (!dataMileage) {
      return null;
    }
    const onChangeHandlerInputs = (id, currentId, object, key, value) => {
      const newObj = { ...object };
      if (id === currentId) {
        newObj[`${key}`] = value;
      }

      if (key === 'workDist' || key === 'personalDist') {
        newObj.odometerDist = Number(newObj.workDist) + Number(newObj.personalDist) + Number(monthMileage.odometerDist);
        newObj.oilNormal = Math.round(fuelNorm * newObj.odometerDist) / 100;
      }

      return newObj;
    };

    const arr = dataMileage.map((item) =>
      onChangeHandlerInputs(item.week, dataMileage[number]?.week, item, row?.key, e?.target?.value),
    );

    let mileage = 0;

    return arr.map((item, i) => {
      const newObj = { ...item };
      mileage =
        mileage + Number(newObj.workDist) + Number(newObj.personalDist) + (!i ? Number(monthMileage.odometerDist) : 0);

      newObj.odometerDist = mileage;
      return newObj;
    });
  };

  const onSaveHandler = useCallback(() => {
    const promises = [];
    const objMonth = {
      // Первый день месяца
      date: new Date(year, month - 1, 1, 0, 0, 0, 0).toISOString(),
      year,
      month,
      fuel: monthMileage.fuel,
      odometerDist: monthMileage.odometerDist,
    };
    if (monthMileage.vehicleMileageId == null) {
      objMonth.vehicle = vehicle;
      promises.push(postVehicleMonthMileagesResourceParams({ body: objMonth }));
    } else {
      objMonth.vehicleMileageId = monthMileage.vehicleMileageId;
      promises.push(
        patchVehicleMonthMileagesResourceParams({
          body: objMonth,
          id: monthMileage.vehicleMileageId,
        }),
      );
    }
    dataMileage.forEach((e, i) => {
      const obj = {
        // Первоый день недели
        date: weekNumbers[i] ? new Date(year, month - 1, weekNumbers[i].start, 0, 0, 0, 0).toISOString() : null,
        year,
        month,
        week: e.week,
        refuel: e.refuel,
        fuelCharge: e.fuelCharge,
        personalDist: e.personalDist,
        workDist: e.workDist,
        gpsDist: e.gpsDist,
        odometerDist: e.odometerDist,
      };
      if (e.vehicleMileageId) {
        obj.vehicleMileageId = e.vehicleMileageId;
        promises.push(
          patchVehicleWeekMileagesResourceParams({
            body: obj,
            id: e.vehicleMileageId,
          }),
        );
      } else {
        obj.vehicle = vehicle;
        promises.push(postVehicleWeekMileagesResourceParams({ body: obj }));
      }
    });
    return Promise.all(promises).then((res) => {
      const error = res.find?.((r) => !!r.error);
      setIsCorrect(!error);
      setIsCreateNewMonthMileage(true);
    });
  }, [monthMileage, dataMileage, isCreateNewMonthMileage]);

  useEffect(() => {
    validateForm();
    setIsCorrect(false);
  }, [year, month, vehicle, dataMileage, fuelNorm]);

  useEffect(() => {
    if (month && year && vehicle) {
      Promise.all([
        ...Array.from({ length: 6 }).map((_, i) =>
          getVehicleWeekMileagesParams({
            params: {
              'vehicleId.equals': vehicle.vehicleId,
              'year.equals': year,
              'month.equals': month,
              'week.equals': i + 1,
            },
          }),
        ),
        getVehicleMonthMileagesParams({
          params: {
            'vehicleId.equals': vehicle.vehicleId,
            'year.equals': year,
            'month.equals': month,
          },
        }),
      ])
        .then((responses) => {
          const result = [];
          const monthMileageResp = responses.find((resp) => resp.endpointName === 'getVehicleMonthMileagesResource');
          const monthMileageData = monthMileageResp.data.data[0];
          responses
            .filter((r) => r.endpointName === 'getVehicleWeekMileagesResource')
            .forEach((e) => {
              if (e.data?.data?.[0]) {
                result.push(e.data.data[0]);
              } else {
                result.push(defaultDataMileage[e.originalArgs.params['week.equals'] - 1]);
              }
            });
          if (monthMileageData) {
            setIsCreateNewMonthMileage(false);
            setMonthMileage(monthMileageData);
          } else {
            setIsCreateNewMonthMileage(true);
            setMonthMileage(defaultMonthMileage);
          }
          setDataMileage(result);
        })
        .catch(() => {
          setDataMileage(defaultDataMileage);
          setMonthMileage(defaultMonthMileage);
        });
    }
  }, [month, year, vehicle]);

  useEffect(() => {
    setWeekNumbers(getMonthWeeks(year, month));
  }, [year, month]);

  const gpsHandler = useCallback(() => {
    if (year && month && vehicle) {
      setIsLoadingGps(true);
      const dateStartCurrent = new Date(Number(year), month - 1, 1, 23, 59, 0);

      const dateFinishCurrent = new Date(Number(year), month, 0, 23, 59, 0);

      const localTimeZoneOffset = dateStartCurrent.getTimezoneOffset() * 60000;

      const isoDateStartCurrent = new Date(dateStartCurrent - localTimeZoneOffset).toISOString();
      const isoDateFinishCurrent = new Date(dateFinishCurrent - localTimeZoneOffset).toISOString();

      getTrackPointsResourceParams({
        params: {
          'vehicleId.equals': vehicle.vehicleId,
          'date.greaterThanOrEqual': isoDateStartCurrent,
          'date.lessThan': isoDateFinishCurrent,
          filters: 'ANOMALY',
        },
      })
        .then((e) => {
          if (e?.data?.data) {
            const arr = splitArrayByWeeks(e.data.data).map((item) => calculateTotalDistance(item));
            setDataMileage((prev) =>
              prev.map((week, i) => {
                const newObj = { ...week };
                newObj.gpsDist = Math.round(arr[i]);
                return newObj;
              }),
            );
          }
        })
        .finally(() => {
          setIsLoadingGps(false);
        });
    }
  }, [year, month, vehicle]);

  useEffect(() => {
    setDataMileage(onChangeHandlerInputs2());
  }, [monthMileage.odometerDist, monthMileage.fuel, fuelNorm]);

  function getInputCellProps(value, row, rowIndex, i) {
    const isError = errorWeekNum === i + 1;
    return {
      disabled: !isTableEnabled || row.key === 'oilNormal' || row.key === 'odometerDist' || row.key === 'gpsDist',

      value: isTableEnabled ? value : '-',
      type: 'number',
      inputProps: { min: 0, style: { textAlign: 'right' } },
      variant: 'outlined',
      size: 'small',
      onChange: (e) => setDataMileage(onChangeHandlerInputs2(e, row, i)),
      onBlur: validateForm,
      error: hasError(isError, rowIndex),
      hasErrorIcon: hasErrorIcon(isError, rowIndex),
      fieldFuelAvailable,
    };
  }

  const handlePrevMonth = () => {
    setMonth((prevMonth) => {
      if (prevMonth === 1) {
        setYear((prevYear) => prevYear - 1);
        return 12;
      }
      return prevMonth - 1;
    });
  };

  const handleNextMonth = () => {
    setMonth((prevMonth) => {
      if (prevMonth === 12) {
        setYear((prevYear) => prevYear + 1);
        return 1;
      }
      return prevMonth + 1;
    });
  };

  return (
    <Box sx={{ paddingBottom: '30px' }}>
      <Box sx={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            gap: '30px',
            marginTop: '30px',
          }}
        >
          <FormControl
            sx={{
              flex: '1',
              minWidth: '200px',
            }}
            fullWidth
          >
            <InputLabel id="typeMessage-label">Выберите год</InputLabel>
            <Select
              labelId="typeMessage-label"
              id="typeMessage-label"
              value={year || ''}
              label="Выберите год"
              onChange={(e) => {
                setYear(e.target.value);
              }}
              size="small"
            >
              {years?.map((yearCurrent) => (
                <MenuItem key={`alertChannelId-select-item${yearCurrent.id}`} value={yearCurrent.id}>
                  {yearCurrent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            sx={{
              flex: '1',
              minWidth: '200px',
            }}
            fullWidth
          >
            <InputLabel id="typeMessage-label">Выберите месяц</InputLabel>
            <Select
              labelId="typeMessage-label"
              id="typeMessage-label"
              value={month || 0}
              label="Выберите месяц"
              onChange={(e) => {
                setMonth(e.target.value);
              }}
              size="small"
            >
              {months?.map((monthCurrent) => (
                <MenuItem key={`alertChannelId-select-item${monthCurrent.id}`} value={monthCurrent.id}>
                  {monthCurrent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconButton size="small" title="Предыдущий месяц" color="primary" onClick={handlePrevMonth}>
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton size="small" title="Следующий месяц" color="primary" onClick={handleNextMonth}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ width: '100%' }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650, '& td, & th': { border: 'none' } }} size="small" aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell>{` `}</TableCell>
                  <TableCell align="center">
                    1 неделя
                    <br />
                    {addLeadingZero(weekNumbers[0]?.start)}.{addLeadingZero(month)} -{' '}
                    {addLeadingZero(weekNumbers[0]?.finish)}.{addLeadingZero(month)}
                  </TableCell>
                  <TableCell align="center">
                    2 неделя
                    <br />
                    {addLeadingZero(weekNumbers[1]?.start)}.{addLeadingZero(month)} -{' '}
                    {addLeadingZero(weekNumbers[1]?.finish)}.{addLeadingZero(month)}
                  </TableCell>
                  <TableCell align="center">
                    3 неделя
                    <br />
                    {addLeadingZero(weekNumbers[2]?.start)}.{addLeadingZero(month)} -{' '}
                    {addLeadingZero(weekNumbers[2]?.finish)}.{addLeadingZero(month)}
                  </TableCell>
                  <TableCell align="center">
                    4 неделя
                    <br />
                    {addLeadingZero(weekNumbers[3]?.start)}.{addLeadingZero(month)} -{' '}
                    {addLeadingZero(weekNumbers[3]?.finish)}.{addLeadingZero(month)}
                  </TableCell>
                  <TableCell align="center">
                    5 неделя
                    <br />
                    {addLeadingZero(weekNumbers[4]?.start)}.{addLeadingZero(month)} -{' '}
                    {addLeadingZero(weekNumbers[4]?.finish)}.{addLeadingZero(month)}
                  </TableCell>
                  {weekNumbers[5]?.start && (
                    <TableCell align="center">
                      6 неделя
                      <br />
                      {addLeadingZero(weekNumbers[5]?.start)}.{addLeadingZero(month)} -{' '}
                      {addLeadingZero(weekNumbers[5]?.finish)}.{addLeadingZero(month)}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow
                    hover
                    key={row.name}
                    sx={{ '&:last-child td, &:last-child th': { border: 0, paddingBottom: 1.5 } }}
                  >
                    <TableCell component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <CellInput textFieldProps={getInputCellProps(row.calories, row, i, 0)} />
                    <CellInput textFieldProps={getInputCellProps(row.fat, row, i, 1)} />
                    <CellInput textFieldProps={getInputCellProps(row.carbs, row, i, 2)} />
                    <CellInput textFieldProps={getInputCellProps(row.protein, row, i, 3)} />
                    <CellInput textFieldProps={getInputCellProps(row.cost, row, i, 4)} />
                    {weekNumbers[5]?.start && <CellInput textFieldProps={getInputCellProps(row.last, row, i, 5)} />}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box sx={{ width: '350px' }}>
          <TextField
            sx={{ width: '100%' }}
            value={monthMileage.odometerDist}
            type="number"
            onChange={(e) => {
              const { value } = e.target;
              setMileageOfNewMonth(value);
            }}
            label="Показания одометра на начало месяца, км"
            variant="outlined"
            size="small"
            disabled={!isTableEnabled}
          />
        </Box>
        <Box sx={{ width: '350px' }}>
          <RoundedNumberField
            sx={{ width: '100%' }}
            value={monthMileage.fuel}
            onChange={(e) => {
              const { value } = e.target;
              setOilOfNewMonth(value);
            }}
            label="Показания топлива на начало месяца, л"
            variant="outlined"
            size="small"
          />
        </Box>
        {isCorrect && (
          <Alert sx={{ width: '100%' }} severity="success">
            <AlertTitle>Успешно</AlertTitle>
            Данные успешно сохранены
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: '30px',
            flexWrap: 'wrap',
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            disabled={!isTableEnabled && !vehicle}
            onClick={() => (!isLoadingGps ? gpsHandler() : null)}
          >
            {isLoadingGps ? <CircularProgress sx={{ color: 'white' }} size={20} /> : 'Пересчитать по GPS'}
          </Button>
          <Button variant="contained" disabled={!isTableEnabled || !!errorWeekNum} onClick={onSaveHandler}>
            Сохранить
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

ReportingPanel.propTypes = {
  postVehicleWeekMileagesResourceParams: PropTypes.func,
  getVehicleWeekMileagesParams: PropTypes.func,
  patchVehicleWeekMileagesResourceParams: PropTypes.func,
  postVehicleMonthMileagesResourceParams: PropTypes.func,
  getVehicleMonthMileagesParams: PropTypes.func,
  patchVehicleMonthMileagesResourceParams: PropTypes.func,
  getTrackPointsResourceParams: PropTypes.func,
  fuelNorm: PropTypes.number,
  vehicle: PropTypes.shape({
    vehicleId: PropTypes.number,
  }),
};

ReportingPanel.defaultProps = {
  postVehicleWeekMileagesResourceParams: null,
  getVehicleWeekMileagesParams: null,
  patchVehicleWeekMileagesResourceParams: null,
  postVehicleMonthMileagesResourceParams: null,
  getVehicleMonthMileagesParams: null,
  patchVehicleMonthMileagesResourceParams: null,
  getTrackPointsResourceParams: null,
  vehicle: null,
  fuelNorm: null,
};

export default ReportingPanel;
